import { useRef, type MouseEvent } from 'react';
import { Logo } from '@/components/logo';
import { Reveal } from '@/components/reveal';
import { shellClass } from '@/components/section';
import { copy } from '@/content/copy';
import { cn } from '@/lib/cn';
import { DOCS_URL } from '@/lib/env';
import { useRevealOnEnter } from '@/scroll/use-reveal-on-enter';
import { useScroll, useSectionLink } from '@/scroll/use-scroll';

export function Footer() {
  const ref = useRef<HTMLElement>(null);
  const { scrollToTop } = useScroll();
  const sectionLink = useSectionLink();

  useRevealOnEnter(
    ref,
    (tl, q) => {
      tl.to(q('[data-reveal]'), { opacity: 1, y: 0, duration: 0.7, stagger: 0.1 }, 0);
    },
    { start: 'top 96%' },
  );

  const onBrandClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    scrollToTop();
  };

  return (
    <footer
      ref={ref}
      className="relative border-t border-border bg-background px-[clamp(1.4rem,5vw,5rem)] py-10"
    >
      <div
        className={cn(
          shellClass,
          'flex flex-col gap-8 md:flex-row md:items-center md:justify-between',
        )}
      >
        <Reveal className="flex flex-col gap-4">
          <a
            href="#top"
            onClick={onBrandClick}
            aria-label={`${copy.brand.name}, ${copy.a11y.backToTop}`}
            className="self-start rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
          >
            <Logo size="footer" />
          </a>
          <p className="hud text-muted-foreground">{copy.brand.tagline}</p>
        </Reveal>

        <Reveal className="flex flex-col gap-4 md:items-end">
          <nav aria-label={copy.a11y.footerNav} className="flex flex-wrap gap-6">
            {copy.footer.links.map((link) => (
              <a
                key={link.id}
                className="nav-link text-sm text-muted-foreground hover:text-foreground"
                {...sectionLink(link.id)}
              >
                {link.label}
              </a>
            ))}
            <a
              href={DOCS_URL}
              className="nav-link text-sm text-muted-foreground hover:text-foreground"
            >
              {copy.footer.docs}
            </a>
          </nav>
          <p className="text-xs text-muted-foreground/80">
            © {new Date().getFullYear()} {copy.brand.name}. {copy.footer.rights}
          </p>
        </Reveal>
      </div>
    </footer>
  );
}
